import type { AgentHarness, AvailablePhone } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import { PROFILE_LABELS } from '@/frontend/pages/Dashboard/DigitalWorkers/constants'

export type CreateWizardStepNumber = 1
export type OnboardingWizardStepNumber = 2 | 3 | 4 | 5 | 6 | 7
export type FullWizardStepNumber = CreateWizardStepNumber | OnboardingWizardStepNumber

export type WhatsAppProviderChoice = 'business' | 'none'
export type { AgentHarness }

export type DigitalWorkerCreateFormData = {
  name: string
  job_title: string
  fleet_profile: string
  contact_name: string
  contact_whatsapp: string
  personality: string
  whatsapp_provider: WhatsAppProviderChoice
  reuse_stationzero_email: boolean
  agent_harness: AgentHarness
}

export type DigitalWorkerCreateField = keyof DigitalWorkerCreateFormData

export type ServerErrorField =
  | DigitalWorkerCreateField
  | 'avatar'
  | 'base'
  | 'instance_name'
  | 'stationzero_email'

export type FieldErrors = Partial<Record<ServerErrorField, string>>

export type FieldChangeHandler = <K extends DigitalWorkerCreateField>(
  field: K,
  value: DigitalWorkerCreateFormData[K],
) => void

export interface BaseStepProps {
  data: DigitalWorkerCreateFormData
  errors: FieldErrors
  disabled?: boolean
  onFieldChange: FieldChangeHandler
}

export interface ProfileStepProps extends BaseStepProps {
  profileOptions?: import('@/frontend/features/AgentProfiles/types').AgentProfileOption[]
  profileCopy?: import('@/frontend/features/AgentProfiles/types').ProfileCopy
  fleetProfiles: string[]
  onShuffleName: () => void
  showEmailProvisioningToggle?: boolean
  showHarnessSelector?: boolean
  showCodexTemplates?: boolean
  agentHarnesses?: AgentHarness[]
}

export interface IdentityStepProps extends BaseStepProps {}

export interface LaunchStepProps extends BaseStepProps {
  availablePhone: AvailablePhone | null
}

export const FULL_WIZARD_STEP_LABELS: Record<FullWizardStepNumber, string> = {
  1: 'Nome e cargo',
  2: 'Assinatura',
  3: 'Responsável',
  4: 'Personalidade',
  5: 'Aparência',
  6: 'Integrações',
  7: 'Primeiro chat',
}

export const CREATE_WIZARD_STEPS = [1] as const
export const ONBOARDING_WIZARD_STEPS = [2, 3, 4, 5, 6, 7] as const
export const FULL_WIZARD_STEPS = [1, 2, 3, 4, 5, 6, 7] as const

const STEP_FIELDS: Record<FullWizardStepNumber, ServerErrorField[]> = {
  1: ['name', 'fleet_profile', 'stationzero_email', 'instance_name'],
  2: [],
  3: ['contact_name', 'contact_whatsapp'],
  4: ['job_title', 'personality'],
  5: ['avatar'],
  6: [],
  7: [],
}

export function stepForField(field: string): FullWizardStepNumber {
  const normalized = field as ServerErrorField
  const match = FULL_WIZARD_STEPS.find((step) => STEP_FIELDS[step].includes(normalized))

  return match ?? 1
}

export function stripWhatsAppDigits(value: string): string {
  return value.replace(/\D/g, '')
}

export function isValidBrazilianWhatsApp(value: string): boolean {
  return /^55\d{10,11}$/.test(stripWhatsAppDigits(value))
}

export function summarizeCreateStep(data: DigitalWorkerCreateFormData): string | undefined {
  return data.name.trim() || PROFILE_LABELS[data.fleet_profile] || undefined
}
